import React, {ReactElement} from 'react';
import {Card, CardContent, makeStyles, Typography} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import HelperUtils from '@shared/utils/HelperUtils';
import {Patient} from '../EligibilityVerificationMapper';
import KeyValueCard from '@components/system/KeyValueCard/KeyValueCard';
import {CARD_BOX_SHADOW} from 'src/style/variable';

const insurancePatientDetailsCard = makeStyles(() => ({
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    },
    boxShadow: CARD_BOX_SHADOW,
    borderRadius: '1rem'
  },
  patientSection: {
    maxWidth: '60rem'
  },
  addressValue: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '0.5rem'
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 600,
    paddingBlock: '1rem'
  }
}));

interface InsurancePatientDetailsCardProps {
  patient: Patient;
}

const InsurancePatientDetailsCard = (
  props: InsurancePatientDetailsCardProps
): ReactElement => {
  const classes = insurancePatientDetailsCard();
  const {patient} = props;
  const {t} = useTranslation();

  const addressItems = [];
  if (patient?.address?.line1) {
    addressItems.push(
      <Typography key="line1">{patient.address?.line1}</Typography>
    );
  }
  if (patient?.address?.line2) {
    addressItems.push(
      <Typography key="line2">{patient.address?.line2}</Typography>
    );
  }
  if (patient?.address?.city) {
    addressItems.push(
      <Typography key="city">{patient.address?.city}</Typography>
    );
  }
  if (
    patient?.address?.state ||
    patient?.address?.stateCode ||
    patient?.address?.zipCode
  ) {
    addressItems.push(
      <Typography>
        {patient?.address?.state ? `${patient?.address?.state}, ` : ''}
        {patient?.address?.stateCode ? `${patient?.address?.stateCode}, ` : ''}
        {patient?.address?.zipCode}
      </Typography>
    );
  }
  const patientData = [
    {
      label: t('ELIGIBILITY_VERIFICATION.NAME'),
      value: patient?.firstName
        ? HelperUtils.formFullName(
            patient?.firstName,
            patient?.lastName,
            patient?.middleName
          )
        : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.MEMBER_ID'),
      value: patient?.memberId ? patient.memberId : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.ACCOUNT_NUMBER'),
      value: patient?.patientAccountNumber ? patient.patientAccountNumber : '-'
    },
    // NOTE: Commented ONLY For DEMO Purpose, Once demo is done, need to uncomment
    // {
    //   label: t('ELIGIBILITY_VERIFICATION.GENDER'),
    //   value: patient?.gender ? patient.gender : '-'
    // },
    {
      label: t('ELIGIBILITY_VERIFICATION.RELATIONSHIP_TO_SUBSCRIBER'),
      value: patient?.subscriberRelationship
        ? patient.subscriberRelationship
        : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.ADDRESS'),
      value: (
        <Typography className={classes.addressValue}>
          {addressItems.length > 0 ? addressItems : '-'}
        </Typography>
      )
    }
  ];

  return (
    <div className={`section-header ${classes.patientSection}`}>
      <Typography className={classes.title}>
        {t('ELIGIBILITY_VERIFICATION.PATIENT')}
      </Typography>
      <Card className={`${classes.cardRoot} card`}>
        <CardContent>
          <KeyValueCard data={patientData} />
        </CardContent>
      </Card>
    </div>
  );
};

export default InsurancePatientDetailsCard;
